import type {
    Breadcrumb,
    BreadcrumbHint,
    ErrorEvent,
    Event,
    EventHint,
    ReplayFrameEvent,
    vueIntegration,
} from '@sentry/vue';
import {logUuid} from './logContext';
import {LOG_REDACTED} from './logSafe/LogSafeValue';

// `@sentry/vue` re-exports no `Integration` type, so it is taken from the SDK's own integration factory.
type Integration = ReturnType<typeof vueIntegration>;

// Pinned rather than inherited, so an SDK bump that flips a default cannot widen what we send.
export const pinnedPrivacyOptions = {
    sendDefaultPii: false,
} as const;

export const pinnedVueIntegrationOptions = {
    // On by default: ships the props of the failing component, which are entities and payloads.
    attachProps: false,
} as const;

export const pinnedReplayOptions = {
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    // The default list plus alt, which carries image descriptions such as a user's name, and href, which
    // carries tokens and colleagues' addresses (MS Teams chat links).
    maskAttributes: ['title', 'placeholder', 'aria-label', 'alt', 'href'],
    beforeAddRecordingEvent: scrubRecordingEvent,
};

export const ignoredErrorMessages = [
    // Benign browser noise: the observer callback couldn't finish within one
    // frame; layout still settles on the next one.
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
    // Rejections without an Error object carry no stacktrace and nothing actionable.
    /Non-Error promise rejection captured/i,
    /Object captured as promise rejection/i,
    // Network noise (offline users, aborted navigations).
    /failed to fetch/i,
    /load failed/i,
    /networkerror when attempting to fetch resource/i,
    // Failed HTTP responses are tracked by the backend; the status-based drop in
    // beforeSend doesn't catch these because they carry no originalException.
    /HTTP Client Error with status code/i,
    // Chunk-load failures of stale clients after a deploy.
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Failed to fetch dynamically imported module/i,
    /Unable to preload CSS/i,
    // Leaflet teardown races while a map unmounts.
    /\(reading '_leaflet_pos'\)/i,
    /\(evaluating 'h\._leaflet_pos'\)/i,
];

// Errors originating from scripts we don't ship (browser extensions).
export const deniedUrls = [
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
    /extensions\//i,
];

const URL_SPAN_ATTRIBUTES = ['url', 'http.url', 'lcp.url'];
// The Vue router integration copies every query value and route param onto the navigation span.
const ROUTE_QUERY_ATTRIBUTE_PREFIX = 'query.';
const ROUTE_PARAM_ATTRIBUTE_PREFIXES = ['params.', 'url.path.parameter.'];
const LCP_ELEMENT_ATTRIBUTE = 'lcp.element';
const CLS_SOURCE_ATTRIBUTE_PREFIX = 'cls.source.';
// Web vitals and interaction spans are named after the element's selector.
const SELECTOR_SPAN_OP_PREFIXES = ['ui.interaction.', 'ui.webvital.'];
// Resource and browser timing spans are named after the full URL.
const URL_DESCRIPTION_SPAN_OP_PREFIXES = ['resource.', 'browser.'];

const UI_CATEGORY_PREFIX = 'ui.';
const UNKNOWN_ELEMENT = '<unknown>';
// Sentry's own limits for a breadcrumb selector.
const SELECTOR_MAX_DEPTH = 5;
const SELECTOR_MAX_LENGTH = 80;
const SELECTOR_SEPARATOR = ' > ';
// Replay's own attribute list minus aria-label, name, alt and title, which carry visible text.
const RECORDED_NODE_ATTRIBUTES = new Set([
    'id',
    'class',
    'role',
    'testId',
    'disabled',
    'aria-disabled',
    'data-sentry-component',
]);
const DROPPED_SPAN_ATTRIBUTES = ['http.query', 'http.fragment'];
const EMAIL_ADDRESS_PATTERN = /[^\s@<>"'()[\]]+@[^\s@<>"'()[\]]+\.[a-z]{2,}/gi;

// Routes that carry a bearer token as a path segment, mirrored by the backend's SentryBeforeSendFilter.
const TOKEN_PATH_PATTERN = /\/(kiosk|check-in|delete-bookings|verify-sso)\/[^/?#]+/g;
// Pages whose URL is a credential for as long as they are open: login hand-offs, OAuth callbacks,
// confirmation and reset links.
const SENSITIVE_PATH_PATTERN =
    /\/(?:kiosk|check-in|delete-bookings|verify-sso|reset-password)\/[^/?#]+|\/(?:confirm-user|callback)(?:[/?#]|$)/;
const SENSITIVE_PARAMETERS = new Set([
    'token',
    'refreshToken',
    'code',
    'state',
    'id_token',
    'access_token',
    'authorization',
    'registration',
    'email',
    'userEmail',
]);
const URL_PARAMETER_NAME_PATTERN = /[?#&]([^=&#?]+)=/g;
const REPLAY_EVENT_TYPE = 'replay_event';

// Module-private: a caller that only strips the query keeps a token that sits in a path segment.
function stripUrlQuery(url: string): string {
    return url.split(/[?#]/, 1)[0];
}

export function scrubUrl(url: string): string {
    return stripUrlQuery(url).replace(TOKEN_PATH_PATTERN, `/$1/${LOG_REDACTED}`);
}

// Session Replay records the page URL in places no hook reaches (the rrweb meta event of every snapshot),
// so a page whose URL is a credential must not be recorded at all.
export function isSensitiveUrl(url: string): boolean {
    if (SENSITIVE_PATH_PATTERN.test(url)) {
        return true;
    }

    return Array.from(url.matchAll(URL_PARAMETER_NAME_PATTERN), match => match[1]).some(name =>
        SENSITIVE_PARAMETERS.has(name)
    );
}

// Replay events skip beforeSend, so their URL list and request context are scrubbed here.
export const replayEventScrubbingIntegration = {
    name: 'ReplayEventScrubbing',
    processEvent(event: Event): Event {
        if (event.type !== REPLAY_EVENT_TYPE) {
            return event;
        }

        const replayEvent = event as Event & {urls?: unknown};

        if (Array.isArray(replayEvent.urls)) {
            replayEvent.urls = replayEvent.urls.map(url =>
                typeof url === 'string' ? scrubUrl(url) : LOG_REDACTED
            );
        }

        scrubRequest(event);

        return event;
    },
} satisfies Integration;

export function scrubBreadcrumb(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null {
    // A console argument is serialized whole, error payloads included.
    if (breadcrumb.category === 'console') {
        return null;
    }

    if (breadcrumb.category?.startsWith(UI_CATEGORY_PREFIX)) {
        breadcrumb.message = describeElementPath(hint?.event?.target);
    }

    const data = breadcrumb.data;

    if (!data) {
        return breadcrumb;
    }

    for (const key of ['url', 'from', 'to']) {
        if (typeof data[key] === 'string') {
            data[key] = scrubUrl(data[key]);
        }
    }

    return breadcrumb;
}

// Selectors are rebuilt rather than parsed: Sentry writes aria-label, title, name and alt values into them
// unescaped, so no pattern can reliably cut them out again.
function describeElementPath(target: unknown): string {
    const segments: string[] = [];
    let element = target instanceof Element ? target : null;
    let length = 0;

    while (element && element.tagName !== 'HTML' && segments.length < SELECTOR_MAX_DEPTH) {
        const segment = describeElement(element);
        const nextLength = length + segment.length + SELECTOR_SEPARATOR.length;

        if (segments.length && nextLength > SELECTOR_MAX_LENGTH) {
            break;
        }

        segments.unshift(segment);
        length = nextLength;
        element = element.parentElement;
    }

    return segments.join(SELECTOR_SEPARATOR) || UNKNOWN_ELEMENT;
}

function describeElement(element: Element): string {
    if (element instanceof HTMLElement) {
        const componentName = element.dataset.sentryComponent ?? element.dataset.sentryElement;

        if (componentName) {
            return componentName;
        }
    }

    return describeTag(element.tagName, element.id, element.getAttribute('class'));
}

function describeTag(tagName: string, id: unknown, className: unknown): string {
    const idSelector = typeof id === 'string' && id ? `#${id}` : '';
    const classSelectors =
        typeof className === 'string'
            ? className
                  .split(/\s+/)
                  .filter(Boolean)
                  .map(name => `.${name}`)
                  .join('')
            : '';

    return `${tagName.toLowerCase()}${idSelector}${classSelectors}`;
}

type RecordedFrameData = {
    node?: {tagName?: unknown; attributes?: Record<string, unknown>};
    url?: unknown;
    previous?: unknown;
};

// Replay builds its own ui.* frames from the DOM and never passes them through beforeBreadcrumb.
export function scrubRecordingEvent(event: ReplayFrameEvent): ReplayFrameEvent {
    if (event.data.tag === 'performanceSpan') {
        const span = event.data.payload;
        const spanData = span.data as RecordedFrameData | undefined;
        span.description = scrubUrl(span.description);

        if (spanData && typeof spanData.previous === 'string') {
            spanData.previous = scrubUrl(spanData.previous);
        }

        return event;
    }

    if (
        event.data.tag !== 'breadcrumb' ||
        !event.data.payload.category.startsWith(UI_CATEGORY_PREFIX)
    ) {
        return event;
    }

    const frame = event.data.payload;
    const frameData = frame.data as RecordedFrameData | undefined;
    const node = frameData?.node;

    if (node && typeof node.tagName === 'string') {
        const attributes = Object.fromEntries(
            Object.entries(node.attributes ?? {}).filter(([key]) =>
                RECORDED_NODE_ATTRIBUTES.has(key)
            )
        );
        node.attributes = attributes;
        frame.message = describeTag(node.tagName, attributes.id, attributes.class);
    } else {
        frame.message = UNKNOWN_ELEMENT;
    }

    if (frameData && typeof frameData.url === 'string') {
        frameData.url = scrubUrl(frameData.url);
    }

    return event;
}

function scrubRequest(event: Event): void {
    const request = event.request;

    if (!request) {
        return;
    }

    delete request.query_string;
    delete request.cookies;
    delete request.data;

    if (request.url) {
        request.url = scrubUrl(request.url);
    }

    if (request.headers?.Referer) {
        request.headers.Referer = scrubUrl(request.headers.Referer);
    }
}

export function scrubEvent<SentryEvent extends Event>(event: SentryEvent): SentryEvent {
    scrubRequest(event);

    // A floor for third-party error text such as MSAL's, which quotes the user's UPN. Our own messages are
    // literals; this is the one denylist, because a library's message is the one thing we cannot type.
    if (event.message) {
        event.message = redactEmailAddresses(event.message);
    }

    for (const exception of event.exception?.values ?? []) {
        if (exception.value) {
            exception.value = redactEmailAddresses(exception.value);
        }
    }

    const rootSpan = event.contexts?.trace;

    if (rootSpan?.data) {
        scrubSpanData(rootSpan.data);
    }

    for (const span of event.spans ?? []) {
        scrubSpan(span);
    }

    return event;
}

function redactEmailAddresses(text: string): string {
    return text.replace(EMAIL_ADDRESS_PATTERN, LOG_REDACTED);
}

type ScrubbableSpan = {description?: string; op?: string; data?: Record<string, unknown>};

// Also wired as beforeSendSpan, the only hook standalone spans such as INP pass through.
export function scrubSpan<Span extends ScrubbableSpan>(span: Span): Span {
    const op = span.op ?? '';

    if (span.description && SELECTOR_SPAN_OP_PREFIXES.some(prefix => op.startsWith(prefix))) {
        span.description = cutSelectorAttributes(span.description);
    }

    if (
        span.description &&
        URL_DESCRIPTION_SPAN_OP_PREFIXES.some(prefix => op.startsWith(prefix))
    ) {
        span.description = scrubUrl(span.description);
    }

    if (span.data) {
        scrubSpanData(span.data);
    }

    return span;
}

function scrubSpanData(data: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(data)) {
        if (DROPPED_SPAN_ATTRIBUTES.includes(key) || key.startsWith(ROUTE_QUERY_ATTRIBUTE_PREFIX)) {
            delete data[key];
            continue;
        }

        // A repeatable route param arrives as an array.
        if (ROUTE_PARAM_ATTRIBUTE_PREFIXES.some(prefix => key.startsWith(prefix))) {
            data[key] = typeof value === 'string' ? logUuid(value) : LOG_REDACTED;
            continue;
        }

        if (typeof value !== 'string') {
            continue;
        }

        if (URL_SPAN_ATTRIBUTES.includes(key)) {
            data[key] = scrubUrl(value);
        } else if (key === LCP_ELEMENT_ATTRIBUTE || key.startsWith(CLS_SOURCE_ATTRIBUTE_PREFIX)) {
            data[key] = cutSelectorAttributes(value);
        }
    }
}

// Sentry writes [aria-label="..."] and friends unescaped after the tag, id and classes, so only the part
// before the first attribute is provably structural.
function cutSelectorAttributes(selector: string): string {
    return selector.split('[', 1)[0].trim() || UNKNOWN_ELEMENT;
}

// Stable fingerprint so non-Error rejections collapse into one issue rather than one per event.
export function fingerprintNonErrorRejection(event: ErrorEvent, hint: EventHint): void {
    const error = hint?.originalException;

    if (!error || typeof error === 'string') {
        return;
    }

    if (typeof (error as Error).message !== 'string') {
        event.fingerprint = ['non-error-rejection'];
    }
}

const throttleLimits = {
    windowMs: 60_000,
    maxPerKey: 5,
    maxTotal: 30,
};

function getThrottleKey(event: ErrorEvent): string {
    if (event.fingerprint?.length) {
        return event.fingerprint.join('|');
    }

    const exceptions = event.exception?.values ?? [];
    const exception = exceptions[exceptions.length - 1];
    const frames = exception?.stacktrace?.frames ?? [];
    const frame = frames[frames.length - 1];

    return [exception?.type, exception?.value, frame?.filename, frame?.function].join('|');
}

// Client-side throttle so an error loop can't drain the Sentry quota before
// server-side protections kick in: identical events are capped per minute,
// with a global cap as safety net for loops producing varying messages.
export function createEventThrottle(): (event: ErrorEvent) => boolean {
    const throttleBuckets = new Map<string, number[]>();
    let throttleTotal: number[] = [];

    return function isThrottled(event: ErrorEvent): boolean {
        const now = Date.now();
        const windowStart = now - throttleLimits.windowMs;

        for (const [key, timestamps] of throttleBuckets) {
            const currentTimestamps = timestamps.filter(timestamp => timestamp > windowStart);
            if (currentTimestamps.length) {
                throttleBuckets.set(key, currentTimestamps);
            } else {
                throttleBuckets.delete(key);
            }
        }
        throttleTotal = throttleTotal.filter(timestamp => timestamp > windowStart);

        if (throttleTotal.length >= throttleLimits.maxTotal) {
            return true;
        }

        const key = getThrottleKey(event);
        const bucket = throttleBuckets.get(key) ?? [];

        if (bucket.length >= throttleLimits.maxPerKey) {
            return true;
        }

        bucket.push(now);
        throttleBuckets.set(key, bucket);
        throttleTotal.push(now);

        return false;
    };
}
